import {
  Account,
  Facebook,
  Instagram,
  Twitter,
  UAGLogo,
  Youtube,
} from "@components/icons"
import colors from "@constants/colors"
import { smallcapsBold13Styles } from "@constants/typography"
import { MobileOnly } from "@utils/MediaQuery"
import { uniqueId } from "lodash-es"
import React from "react"
import styled from "styled-components"

import {
  Copyright,
  ExternalFooterLinkComponent,
  Footer,
  FooterLink,
  FooterLinkProps,
  FooterProps,
  Header,
  Legal,
  LegalGroup,
  LegalLink,
  Logo,
  Main,
  Social,
  SocialIcon,
  SocialIconWrapper,
  User,
  UserIcon,
} from "./common"

const Column = styled.div`
  display: flex;
  width: calc(50% - 6px);
  flex-direction: column;
  flex-grow: 0;
  flex-shrink: 0;
`

const ColumnHeader = styled.a`
  ${smallcapsBold13Styles}
  display: flex;
  height: 48px;
  margin-bottom: 12px;
  align-items: center;
  color: ${colors.offWhite};
`

const ColumnHeaderText = styled.span`
  padding-top: 4px;
`

const ColumnGroupSpacer = styled.div`
  margin-bottom: 24px;
`

const ColumnDivider = styled.div`
  width: 12px;
`

const currentYear = new Date().getFullYear()

const MobileFooter: React.FC<FooterProps> = ({
  footerBusinessLabelLink,
  footerBusinessLinks,
  footerExploreLabelLink,
  footerExploreLinks,
  footerShopLabelLink,
  footerShopLinks,
  footerSupportLabelLink,
  footerSupportLinks,
  legalLinks,
  socialLinks,
  user,
}) => {
  const renderColumnHeader = (link: FooterLinkProps) => (
    <ColumnHeader>
      <ColumnHeaderText>{link.text}</ColumnHeaderText>
    </ColumnHeader>
  )

  const renderFooterLinks = (links: Array<FooterLinkProps>) =>
    links.length > 0 &&
    links.map((link: FooterLinkProps) => (
      <FooterLink key={uniqueId()} {...link} />
    ))

  const renderSocialLink = (
    socialUrl: string | undefined,
    IconComponent: React.ReactNode,
    props?: React.AnchorHTMLAttributes<HTMLAnchorElement>
  ) =>
    socialUrl && (
      <SocialIconWrapper
        href={socialUrl}
        rel="noopener"
        target="_blank"
        {...props}
      >
        <SocialIcon>{IconComponent}</SocialIcon>
      </SocialIconWrapper>
    )

  const { facebook, instagram, twitter, youtube } = socialLinks || {}

  const { limitationsOfLiability, privacyPolicy, termsAndConditions } =
    legalLinks || {}

  return (
    <MobileOnly>
      <Footer>
        <Header>
          <Logo to="/">
            <UAGLogo />
          </Logo>
          <User to="/account">
            {user}
            <UserIcon>
              <Account />
            </UserIcon>
          </User>
        </Header>
        <Main>
          <Column>
            {footerShopLabelLink && renderColumnHeader(footerShopLabelLink)}
            {footerShopLinks && renderFooterLinks(footerShopLinks)}
            <ColumnGroupSpacer />
          </Column>
          <ColumnDivider />
          <Column>
            {footerBusinessLabelLink &&
              renderColumnHeader(footerBusinessLabelLink)}
            {footerBusinessLinks && renderFooterLinks(footerBusinessLinks)}
            <ColumnGroupSpacer />
          </Column>
          <Column>
            {footerExploreLabelLink &&
              renderColumnHeader(footerExploreLabelLink)}
            {footerExploreLinks && renderFooterLinks(footerExploreLinks)}
            <ColumnGroupSpacer />
          </Column>
          <ColumnDivider />
          <Column>
            {footerSupportLabelLink &&
              renderColumnHeader(footerSupportLabelLink)}
            {footerSupportLinks && renderFooterLinks(footerSupportLinks)}
            <ExternalFooterLinkComponent
              className="termly-display-preferences"
              href="#"
              id="mobileTermlyButton"
            >
              <span>Consent Preferences</span>
            </ExternalFooterLinkComponent>
            <ColumnGroupSpacer />
          </Column>
        </Main>
        <Social>
          {instagram &&
            renderSocialLink(instagram, <Instagram />, {
              "aria-label": "Instagram",
            })}
          {twitter &&
            renderSocialLink(twitter, <Twitter />, { "aria-label": "Twitter" })}
          {facebook &&
            renderSocialLink(facebook, <Facebook />, {
              "aria-label": "Facebook",
            })}
          {youtube &&
            renderSocialLink(youtube, <Youtube />, { "aria-label": "Youtube" })}
        </Social>
        <Legal>
          <LegalGroup>
            {privacyPolicy && (
              <LegalLink to={privacyPolicy}>Privacy Policy</LegalLink>
            )}
            {limitationsOfLiability && (
              <LegalLink to={limitationsOfLiability}>
                Limitations of Liability
              </LegalLink>
            )}
          </LegalGroup>
          {termsAndConditions && (
            <LegalLink to={termsAndConditions}>Terms and Conditions</LegalLink>
          )}
        </Legal>
        <Copyright>{`© ${currentYear} URBAN ARMOR GEAR, LLC. ALL RIGHTS RESERVED.`}</Copyright>
      </Footer>
    </MobileOnly>
  )
}

export default MobileFooter
